import { useRef, useState, useEffect } from 'react';
import throttle from 'lodash.throttle';

var computeDirection = function computeDirection(_ref) {
  var isRef = _ref.isRef,
      currentY = _ref.currentY,
      currentX = _ref.currentX,
      lastY = _ref.lastY,
      lastX = _ref.lastX;

  // Set NONE if both positions stay the same
  if (currentY === lastY && currentX === lastX) {
    return 'NONE';
  } // Ref case


  if (isRef) {
    if (currentX === lastX) {
      // Scroll on Y axis
      return lastY > currentY ? 'DOWN' : 'UP';
    } // Scroll on X axis


    return lastX > currentX ? 'RIGHT' : 'LEFT';
  } // Window case


  if (currentX === lastX) {
    // Scroll on Y axis
    return lastY < currentY ? 'DOWN' : 'UP';
  } // Scroll on X axis


  return lastX < currentX ? 'RIGHT' : 'LEFT';
};

var getDirectionBooleans = function getDirectionBooleans(scrollDirection) {
  return {
    isScrolling: scrollDirection !== 'NONE',
    isScrollingUp: scrollDirection === 'UP',
    isScrollingDown: scrollDirection === 'DOWN',
    isScrollingLeft: scrollDirection === 'LEFT',
    isScrollingRight: scrollDirection === 'RIGHT',
    scrollDirection: scrollDirection
  };
};

var TIMEOUT_ID = '__USE_SCROLL_DIRECTION_TIMER';

var initializeResetDirection = function initializeResetDirection() {
  window[TIMEOUT_ID] = null;
};
var handleResetDirection = function handleResetDirection(timeToReset, onReset) {
  clearTimeout(window[TIMEOUT_ID]);
  window[TIMEOUT_ID] = setTimeout(onReset, timeToReset);
};

var useScrollDirection = function useScrollDirection(options) {
  var wait = (options == null ? void 0 : options.wait) || 50;
  var timeToReset = (options == null ? void 0 : options.timeToReset) || 150;
  var ref = (options == null ? void 0 : options.ref) || null;
  var lastY = useRef(null);
  var lastX = useRef(null);

  var _useState = useState('NONE'),
      direction = _useState[0],
      setDirection = _useState[1];

  var handleReset = function handleReset() {
    lastY.current = null;
    lastX.current = null;
    setDirection('NONE');
  };

  var handleWindowScroll = function handleWindowScroll() {
    var currentY = Math.round(window.pageYOffset);
    var currentX = Math.round(window.pageXOffset); // Skip first iteration

    if (lastY.current === null || lastX.current === null) {
      lastY.current = currentY;
      lastX.current = currentX;
      return;
    }

    var newDirection = computeDirection({
      isRef: false,
      currentY: currentY,
      currentX: currentX,
      lastY: lastY.current,
      lastX: lastX.current
    });
    setDirection(newDirection); // Reassign lastPosition

    lastY.current = currentY;
    lastX.current = currentX;
    return;
  };

  var handleRefScroll = function handleRefScroll() {
    var _ref$current, _ref$current$children;

    var childRect = ref == null ? void 0 : (_ref$current = ref.current) == null ? void 0 : (_ref$current$children = _ref$current.children) == null ? void 0 : _ref$current$children[0].getBoundingClientRect();

    if (childRect) {
      var currentY = Math.round(childRect.y);
      var currentX = Math.round(childRect.x); // Skip first iteration

      if (lastY.current === null || lastX.current === null) {
        lastY.current = currentY;
        lastX.current = currentX;
        return;
      }

      var newDirection = computeDirection({
        isRef: true,
        currentY: currentY,
        currentX: currentX,
        lastY: lastY.current,
        lastX: lastX.current
      });
      setDirection(newDirection); // Reassign lastY

      lastY.current = currentY;
      lastX.current = currentX;
      return;
    }
  };

  var handleScroll = throttle(function () {
    handleResetDirection(timeToReset, handleReset);

    if (ref) {
      return handleRefScroll();
    }

    return handleWindowScroll();
  }, wait, {
    trailing: false
  });
  useEffect(function () {
    initializeResetDirection();
    var scrollContext = (ref == null ? void 0 : ref.current) || window;
    scrollContext.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return function () {
      return scrollContext.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return getDirectionBooleans(direction);
};

export { useScrollDirection };
//# sourceMappingURL=use-scroll-direction.esm.js.map
